import classNames from 'classnames';

const colWidths = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

type ColWidth = (typeof colWidths)[number];

type ColumnSize =
  | boolean
  | number
  | string
  | {
      size?: boolean | number | string;
      order?: number | string;
      offset?: number | string;
    };

interface ColProps extends React.HTMLAttributes<HTMLElement> {
  /** Make the html be a different tag, such as `<span>` */
  tag?: React.ElementType;
  /** The portion of the total width of the column on extra small screens */
  xs?: ColumnSize;
  /** The portion of the total width of the column on small screens */
  sm?: ColumnSize;
  /** The portion of the total width of the column on medium screens */
  md?: ColumnSize;
  /** The portion of the total width of the column (out of 12 columns) on large screens */
  lg?: ColumnSize;
  /** The portion of the total width of the column on extra large screens */
  xl?: ColumnSize;
  /** Available widths of the column based on screen size */
  widths?: readonly ColWidth[];
  /** Constrains the width of the Col to its content */
  fixed?: boolean;
  /** Identifier used by QE */
  'data-qe-id'?: string;
}

const defaultProps: Partial<ColProps> = {
  tag: 'div',
  widths: colWidths,
  xs: undefined,
  sm: undefined,
  md: undefined,
  lg: undefined,
  xl: undefined,
  fixed: false,
  'data-qe-id': undefined,
};

const getColumnSizeClass = (isXs: boolean, colWidth: string, colSize: boolean | string | number): string => {
  if (colSize === true || colSize === '') {
    return isXs ? 'col' : `col-${colWidth}`;
  }

  if (colSize === 'auto') {
    return isXs ? 'col-auto' : `col-${colWidth}-auto`;
  }

  if (colSize === 'fixed') {
    return isXs ? 'col-auto col-fixed' : `col-${colWidth}-auto`;
  }

  return isXs ? `col-${colSize}` : `col-${colWidth}-${colSize}`;
};

export const Col: React.FC<ColProps> = (props) => {
  const { className, widths = colWidths, tag: Tag = 'div', fixed, 'data-qe-id': qeId, ...attributes } = props;
  const colClasses: string[] = [];

  widths.forEach((colWidth, i) => {
    const columnProp = props[colWidth];

    delete attributes[colWidth];

    if (!columnProp && columnProp !== '') {
      return;
    }

    const isXs = !i;

    if (typeof columnProp === 'object' && columnProp !== null) {
      // { size: Boolean|Number|String, order: String|Number, offset: String|Number }
      const colSizeInterfix = isXs ? '-' : `-${colWidth}-`;
      const colClass = getColumnSizeClass(isXs, colWidth, columnProp.size ?? '');

      colClasses.push(
        classNames({
          [colClass]: columnProp.size || columnProp.size === '',
          [`order${colSizeInterfix}${columnProp.order}`]: columnProp.order || columnProp.order === 0,
          [`offset${colSizeInterfix}${columnProp.offset}`]: columnProp.offset || columnProp.offset === 0,
        })
      );
    } else {
      // columnProp is Boolean, Number, or String
      const colClass = getColumnSizeClass(isXs, colWidth, columnProp);

      colClasses.push(colClass);
    }
  });

  if (!colClasses.length) {
    colClasses.push('col');
  }

  const classes = classNames(className, colClasses, { 'col-auto': fixed });

  return <Tag {...attributes} className={classes} data-qe-id={qeId} />;
};

Col.defaultProps = defaultProps;
Col.displayName = 'Col';

export default Col;
